import { useState, ChangeEvent } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ChevronRight } from 'lucide-react'
import { Turnstile } from '@marsidev/react-turnstile'
import * as env from '@/env'
import { toast } from 'react-hot-toast'
import { Loader2 } from 'lucide-react'
import Modal from '../modal'
import Terms from '../../pages/popups/terms'
import Privacy from '../../pages/popups/privacy'
import { userFacingErrorMessage } from '@/lib/user-facing-error'

type EnterEmailMode = 'signup' | 'login'

interface EnterEmailProps {
	mode: EnterEmailMode
	email: string
	setEmail: (email: string) => void
	onContinue: () => void, 
	setOtpChallengeId: (otpChallengeId: string) => void
}

export function EnterEmail(props: EnterEmailProps) {
	const devTurnstileSiteKey = '1x00000000000000000000AA'
	const stagingTurnstileSiteKey = '0x4AAAAAAAklWnwo0zUOtnIF'
	const prodTurnstileSiteKey = '0x4AAAAAACJsRn4g6KePolaM'

	let turnstileSiteKey = ''
	if (env.isDevelopment) {
		turnstileSiteKey = devTurnstileSiteKey
	} else if (env.isStaging) {
		turnstileSiteKey = stagingTurnstileSiteKey
	} else {
		turnstileSiteKey = prodTurnstileSiteKey
	}

	const [isLoading, setIsLoading] = useState<boolean>(false)
	const [turnstileToken, setTurnstileToken] = useState<string | null>(null)
	const [acceptTerms, setAcceptTerms] = useState<boolean>(false)
	const [acceptPrivacy, setAcceptPrivacy] = useState<boolean>(false)
	const [acceptAge, setAcceptAge] = useState<boolean>(false)
	const [termsOpen, setTermsOpen] = useState<boolean>(false)
	const [privacyOpen, setPrivacyOpen] = useState<boolean>(false)

	const isValidEmail = (email: string): boolean => {
		return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
	}

	const isContinueEnabled = () => {
		if (!turnstileToken || !isValidEmail(props.email) || isLoading)
			return false
		if (props.mode === 'signup') { 
			if (!acceptTerms || !acceptPrivacy || !acceptAge)
				return false
		}
		return true
	}

	const handleContinue = async (): Promise<void> => {
		setIsLoading(true)
		try {
			const response = await fetch(
				`${env.restBaseUrl}/api/v1/request-otp`, 
				{
					method: 'POST',
					credentials: 'include', 
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({ 
						email: props.email, 
						turnstileToken, 
						isSignup: props.mode === 'signup' 
					})
				})
			if (!response.ok) {
				//	the server's message is the whole point of the typed errors in request-otp:
				//	"Please wait before requesting another passcode", "Sorry, signup is temporarily
				//	disabled". Toasting a fixed string over it told a rate-limited user to try again
				//	later -- the one thing that cannot help -- and made the killswitch look like a
				//	server fault (ha-616). Parsed INSIDE this branch and guarded, because an error
				//	response that is not JSON would otherwise throw a parse error and arrive here as
				//	an unrelated failure. Only `message` is read, never the raw body.
				const body = await response.json().catch(() => undefined)
				throw new Error(typeof body?.message === 'string' && body.message
					? body.message
					: 'We could not send your passcode. Please try again.')
			}
			//	guarded like the error-path parse above. an empty or non-JSON 2xx body throws a
			//	SyntaxError out of here, and ha-616 made that reach the user as the toast
			//	"Unexpected end of JSON input" -- before it, the catch showed a fixed string. The
			//	`!otpChallengeId` branch below already handles an undefined body correctly, so
			//	guarding the parse is the whole fix here (ha-617).
			const data = await response.json().catch(() => undefined)
			const otpChallengeId = data?.otpChallengeId
			if (!otpChallengeId) {
				//	every throw in this block is toasted verbatim, so this one carries the user's
				//	wording and the diagnosis goes to the console. A developer string here would be
				//	shown to the user by the catch below.
				console.error('request-otp returned no otpChallengeId:', data)
				throw new Error('We could not send your passcode. Please try again.')
			}
			props.setOtpChallengeId(otpChallengeId)
			props.onContinue()
		} catch (error) {
			//	the Errors thrown above all carry text written for a user, and
			//	userFacingErrorMessage() keeps a platform-thrown one from being shown beside them:
			//	a dead connection rejects with TypeError ("Failed to fetch", observed while
			//	building ha-616), and a non-JSON body throws SyntaxError. This comment used to
			//	claim only the throws above could reach here, which was not true of the unguarded
			//	ok-path parse sitting between them (ha-617).
			toast.error(userFacingErrorMessage(error) ?? 'We could not send your passcode. Please try again.')
			console.error('Error requesting OTP:', error)
		} finally {
			setIsLoading(false)
		}
	}

	const handleTurnstileSuccess = (newToken: string): void => {
		setTurnstileToken(newToken)
	}

	const handleTurnstileError = (): void => {
		setTurnstileToken(null)
	}

	const handleTurnstileExpire = (): void => {
		setTurnstileToken(null)
	}

	return (
		<div className="space-y-4">			
			<label className="block text-sm font-normal text-gray-700">
				Please enter your email address.
			</label>			
			{props.mode === 'signup' && (
				<label className="block text-sm font-normal text-gray-700">
					We never share your email address with anyone.
				</label>			
			)}
			<Input
				type="email"
				autoComplete="email"
				maxLength={254}
				placeholder="your@email.com"
				value={props.email}
				onChange={(e: ChangeEvent<HTMLInputElement>) => props.setEmail(e.target.value)}
			/>
			{props.mode === 'signup' && (
				<div className="space-y-2 pl-2">
					<div className="flex items-center">
						<input
							type="checkbox"
							id="terms"
							checked={acceptTerms}
							onChange={(e) => setAcceptTerms(e.target.checked)}
							className="mr-2"
						/>
						<label className="text-sm text-gray-700">I accept the <span className="text-rose-600 hover:text-rose-600/80 p-0 h-auto text-sm cursor-pointer" onClick={() => setTermsOpen(true)}>Terms of Service</span></label>
					</div>
					<div className="flex items-center">
						<input
							type="checkbox"
							id="privacy"
							checked={acceptPrivacy}
							onChange={(e) => setAcceptPrivacy(e.target.checked)}
							className="mr-2"
						/>
						<label className="text-sm text-gray-700">I accept the <span className="text-rose-600 hover:text-rose-600/80 p-0 h-auto text-sm cursor-pointer" onClick={() => setPrivacyOpen(true)}>Privacy Policy</span></label>
					</div>
					<div className="flex items-center">
						<input
							type="checkbox"
							id="age"
							checked={acceptAge}
							onChange={(e) => setAcceptAge(e.target.checked)}
							className="mr-2"
						/>
						<label htmlFor="age" className="text-sm text-gray-700">
							I confirm that I am 18 years or older
						</label>
					</div>
				</div>
			)}
			<Modal open={termsOpen} onOpenChange={setTermsOpen}>
				<Terms />
			</Modal>
			<Modal open={privacyOpen} onOpenChange={setPrivacyOpen}>
				<Privacy />
			</Modal>
			<div className="flex justify-center">
				<Turnstile
					siteKey={turnstileSiteKey}
					onSuccess={handleTurnstileSuccess}
					onError={handleTurnstileError}
					onExpire={handleTurnstileExpire}
				/>
			</div>
			<div className="flex justify-end">
				<Button
					onClick={handleContinue}
					disabled={!isContinueEnabled()}
					className="bg-green-500 hover:bg-green-600 text-white flex items-center justify-center w-32"
				>
					{isLoading ? (
						<>
							<Loader2 className="mr-2 h-4 w-4 animate-spin" />
						</>
					) : (
						<>
							Continue
							<ChevronRight className="ml-2 h-4 w-4" />
						</>
					)}
				</Button>
			</div>
		</div>
	)
}
